import { Box } from '@mui/material';
import { useLayout } from '@orgnc/core/hooks';
import Typography from '@mui/material/Typography';
import AppBar from '@mui/material/AppBar';
import * as React from 'react';
import { Logo } from '@orgnc/core/components/atoms';
import SocialList from '@/components/header/SocialList';
import { useEffect, useRef, useState } from 'react';
import GoogleAdTag from '@/components/GoogleAdTag';
import NavBar from './NavBar';
import { useAimGeneralOptions } from '../../../utils/useAimGeneralOptions';
import HeaderCard, { HeaderCardProps } from '../HeaderCard';

const CARD_STYLES: HeaderCardProps['sx'] = {
  image: {
    display: 'flex',
    alignItems: 'center',
    width: {
      sm: '60px',
      md: '110px',
    },
    height: '170px',
  },
};

const Header = () => {
  const { menus, networks, logos } = useLayout();
  const appBarRef = useRef<HTMLDivElement>(null);
  const [headerHeight, setHeaderHeight] = useState<number>(0);
  const { headerCardsByColumn } = useAimGeneralOptions();

  useEffect(() => {
    setHeaderHeight(appBarRef?.current?.offsetHeight || 0);
  }, [appBarRef]);

  return (
    <>
      <AppBar
        ref={appBarRef}
        position="relative"
        sx={{
          zIndex: ({ zIndex }) => zIndex.drawer - 1,
        }}
      >
        <Box
          sx={{
            justifyContent: 'space-between',
            mt: 1,
            mx: 2,
            display: {
              xs: 'none',
              sm: 'flex',
            },
            a: {
              textDecoration: 'unset',
              color: 'brand.text',
            },
          }}
        >
          <SocialList networks={networks} size={'small'} />
          <Box
            sx={{
              display: 'flex',
              gap: '1rem',
            }}
          >
            {menus.HEADER_TOP_NAVIGATION?.map(
              (item) =>
                item.path && (
                  <Typography
                    key={`${item.label}.${item.path}`}
                    variant="body1"
                    component="a"
                    href={item.path}
                    target={item.target || ''}
                    rel={item.target ? 'noopener' : undefined}
                    sx={{
                      transition: 'opacity 0.3s ease-in-out',
                      '&:hover': {
                        opacity: '.6',
                      },
                    }}
                  >
                    {item.label}
                  </Typography>
                )
            )}
          </Box>
        </Box>
        <Box
          sx={{
            display: {
              xs: 'none',
              sm: 'flex',
            },
            maxWidth: '100%',
            mx: 0,
            pr: 9,
            my: {
              md: 1,
              sm: 0,
            },
            width: '100%',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          {headerCardsByColumn && headerCardsByColumn.left.length > 0 && (
            <Box sx={{ display: 'flex' }}>
              {headerCardsByColumn?.left.map((card, idx) => (
                <HeaderCard
                  key={`left-${idx}`}
                  headerCard={card}
                  sx={CARD_STYLES}
                  aspectRatio="2:3"
                />
              ))}
            </Box>
          )}
          <Box
            sx={{
              flexGrow: 1,
              maxWidth: {
                md: '700px',
                sm: '70%',
              },
            }}
          >
            {logos.main?.sourceUrl && (
              <Logo
                sourceUrl={logos.main.sourceUrl}
                width={700}
                height={180}
                title={logos.main.altText}
                sx={{
                  maxWidth: {
                    sm: '100%',
                    md: '700px',
                  },
                  width: '100%',
                  display: 'block',
                  '& img': {
                    width: '100%',
                    height: 'auto',
                    objectFit: 'contain',
                    maxWidth: '100%',
                  },
                }}
              />
            )}
          </Box>
          {headerCardsByColumn && headerCardsByColumn.right.length > 0 && (
            <Box sx={{ display: 'flex' }}>
              {headerCardsByColumn?.right.map((card, idx) => (
                <HeaderCard
                  key={`right-${idx}`}
                  headerCard={card}
                  sx={CARD_STYLES}
                  aspectRatio="2:3"
                />
              ))}
            </Box>
          )}
        </Box>
        <NavBar
          menu={menus.MAIN_NAVIGATION}
          categoriesMenu={menus.HEADER_NAVIGATION}
          networks={networks}
          additionalMenu={menus.FOOTER_NAVIGATION}
          logo={logos.main || undefined}
          headerHeight={headerHeight}
        />
      </AppBar>
      <GoogleAdTag
        zone="header"
        sizes={[
          [300, 50],
          [300, 100],
          [728, 90],
          [970, 90],
          [970, 250],
        ]}
        mapping={[
          [
            [1024, 768],
            [
              [728, 90],
              [970, 90],
              [970, 250],
            ],
          ],
          [[768, 0], [[728, 90]]],
          [
            [0, 0],
            [
              [300, 50],
              [300, 100],
            ],
          ],
        ]}
        preserveSpace={false}
      />
    </>
  );
};

export default Header;
